type ResourceStoreEvents = Readonly<{ on: (event: 'added', listener: (lng: string, ns: string, resources: unknown) => void) => void }>;

/** Per store, the `lng|ns` bundles whose last write was a failed read's empty stand-in. */
const failedWritesByStore = new WeakMap<object, Set<string>>();

/**
 * On a 5xx or a network error, i18next-http-backend answers `(err, true)`: `true` is a retry flag.
 * Once i18next's own retries run out, `Connector.loaded` stores that `true` as an empty bundle, so
 * `hasResourceBundle` says yes for a bundle that failed. Its `-1` load state doesn't last: the next
 * `loadNamespaces`/`changeLanguage` sees a bundle and marks it loaded. So the store's own writes are
 * the record: writing the flag marks the bundle failed, writing real data clears the mark.
 *
 * Starts listening on the first call for a store and returns the same set after that. i18n.ts calls
 * it right after `init` creates the store, before the first bundle request, so no failure is missed.
 */
export const trackFailedBundleWrites = (store: unknown): ReadonlySet<string> => {
  if (!store || typeof (store as Partial<ResourceStoreEvents>).on !== 'function') return new Set();
  const existing = failedWritesByStore.get(store);
  if (existing) return existing;
  const marks = new Set<string>();
  // `addResource` emits `(lng, ns, key, value)`, so only the exact flag marks a failure.
  (store as ResourceStoreEvents).on('added', (lng, ns, resources) => {
    if (resources === true) marks.add(`${lng}|${ns}`);
    else if (resources && typeof resources === 'object') marks.delete(`${lng}|${ns}`);
  });
  failedWritesByStore.set(store, marks);
  return marks;
};

/** Test hook: whether a store is already tracked, without starting to track it. */
export const isTrackingFailedBundleWrites = (store: object): boolean => failedWritesByStore.has(store);
